import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import CurriculumContent from "../../components/CurriculumContent/CurriculumContent";

// Attach the login token so the backend knows who is asking
const authHeaders = () => {
  const token =
    localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
import "./curriculum.css";
import "./curriculum-details.css";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// Expand a grade value such as "JSS1-JSS3", "JSS1 - SS3", "JSS 2" or
// ["JSS1", "JSS2"] into individual class codes (JSS1, JSS2, JSS3, ...).
const expandGrades = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(expandGrades);
  if (typeof value !== "string") return [];
  const order = ["JSS1", "JSS2", "JSS3", "SS1", "SS2", "SS3"];
  const codes = (value.toUpperCase().match(/\b(JSS|SSS|SS)\s*([1-3])\b/g) || [])
    .map((c) => c.replace(/\s+/g, "").replace(/^SSS/, "SS"));
  if (codes.length === 2 && /[-\u2013]|\bTO\b/i.test(value)) {
    const start = order.indexOf(codes[0]);
    const end = order.indexOf(codes[1]);
    if (start !== -1 && end !== -1 && start <= end) {
      return order.slice(start, end + 1);
    }
  }
  return codes;
};

// Count distinct grade levels actually present in the loaded data
const countGradeLevels = (data) => {
  if (!data) return 0;
  const grades = new Set(expandGrades(data.classes));
  const items = data.data || data.curriculum_items || [];
  items.forEach((item) => {
    [item.grade, item.class, item.classes, item.grade_level, item.level]
      .flatMap(expandGrades)
      .forEach((g) => grades.add(g));
  });
  return grades.size;
};

const countTopics = (data) => {
  if (!data) return 0;
  const explicit = Number(data.topics_count || data.count);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  if (Array.isArray(data.topics) && data.topics.length) return data.topics.length;
  const items = data.data || data.curriculum_items || [];
  const set = new Set();
  items.forEach((item) =>
    (Array.isArray(item.topics) ? item.topics : []).forEach((t) => {
      if (typeof t === "string" && t.trim()) set.add(t.trim().toLowerCase());
    })
  );
  return set.size;
};

const CurriculumViewer = () => {
  const [curriculumData, setCurriculumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("All");
  const [subjectDetails, setSubjectDetails] = useState(null);
  const [loadingSubject, setLoadingSubject] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const navigate = useNavigate();

  // Fallback data
  const fallbackData = {
    subjects: [
      "Mathematics",
      "English",
      "Science",
      "Biology",
      "Chemistry",
      "Physics",
    ],
    classes: ["JSS1", "JSS2", "JSS3", "SS1", "SS2", "SS3"],
    curriculum_items: [
      {
        name: "Mathematics",
        grade: "JSS1-SS3",
        topics: [
          "Algebra",
          "Geometry",
          "Trigonometry",
          "Calculus",
          "Statistics",
          "Number Theory",
        ],
        learning_objectives: [
          "Solve algebraic equations and inequalities",
          "Apply geometric principles to real-world problems",
          "Understand trigonometric functions and their applications",
        ],
      },
      {
        name: "English",
        grade: "JSS1-SS3",
        topics: [
          "Grammar",
          "Literature",
          "Composition",
          "Reading Comprehension",
          "Vocabulary",
          "Creative Writing",
        ],
        learning_objectives: [
          "Master parts of speech and sentence structure",
          "Analyze literary texts and themes",
          "Write clear and coherent compositions",
        ],
      },
      {
        name: "Biology",
        grade: "JSS1-SS3",
        topics: [
          "Cell Biology",
          "Genetics",
          "Ecology",
          "Human Anatomy",
          "Plant Biology",
          "Evolution",
        ],
        learning_objectives: [
          "Understand cell structure and function",
          "Explain genetic inheritance patterns",
          "Describe ecological relationships and ecosystems",
        ],
      },
      {
        name: "Chemistry",
        grade: "JSS1-SS3",
        topics: [
          "Atomic Structure",
          "Chemical Bonding",
          "Acids and Bases",
          "Organic Chemistry",
          "Thermodynamics",
        ],
        learning_objectives: [
          "Describe atomic structure and electron configuration",
          "Explain types of chemical bonds",
          "Identify acids, bases and their properties",
        ],
      },
      {
        name: "Physics",
        grade: "JSS1-SS3",
        topics: [
          "Mechanics",
          "Waves",
          "Electricity",
          "Magnetism",
          "Thermodynamics",
          "Quantum Physics",
        ],
        learning_objectives: [
          "Apply Newton's laws of motion",
          "Understand wave properties and behavior",
          "Explain electrical circuits and Ohm's law",
        ],
      },
      {
        name: "Science",
        grade: "JSS1-JSS3",
        topics: [
          "Scientific Method",
          "Matter and Energy",
          "Earth Science",
          "Environmental Science",
          "Technology",
        ],
        learning_objectives: [
          "Apply the scientific method to investigations",
          "Understand properties of matter and energy",
          "Explain Earth's systems and processes",
        ],
      },
    ],
    topics_count: 36,
    last_updated: new Date().toISOString().split("T")[0],
  };

  // Function to clean and deduplicate learning objectives
  const cleanLearningObjectives = (objectives) => {
    if (!Array.isArray(objectives)) return [];

    const seen = new Set();
    const cleaned = [];
    objectives.forEach((obj) => {
      let cleanObj = String(obj).trim();
      if (/resources?:/i.test(cleanObj)) return;

      const sentences = cleanObj
        .split(".")
        .map((s) => s.trim())
        .filter(Boolean);

      sentences.forEach((sentence) => {
        const simplified = sentence
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, " ");

        if (sentence.length > 5 && !seen.has(simplified)) {
          seen.add(simplified);
          cleaned.push(sentence + ".");
        }
      });
    });
    return cleaned.slice(0, 5);
  };

  // Fetch curriculum from API
  useEffect(() => {
    const fetchCurriculum = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Loading curriculum from API...");

        // Try to fetch from backend API
        try {
          const response = await fetch(`${API_URL}/api/agents/curriculum/data`, { headers: authHeaders() });

          if (response.ok) {
            const apiData = await response.json();
            console.log("Successfully fetched from API:", apiData);

            // Validate the API response has required fields
            if (apiData.data && apiData.subjects) {
              setCurriculumData(apiData);
              setLoading(false);
              return;
            }
          }
        } catch (apiError) {
          console.log("API fetch failed, trying fallback:", apiError.message);
        }

        // If API fails, use fallback data
        console.log("Using fallback curriculum data");
        setUsingFallback(true);
        setCurriculumData(fallbackData);
        setLoading(false);
      } catch (err) {
        console.error("Curriculum error:", err);
        setError("Failed to load curriculum data");
        // Still show fallback on error
        setUsingFallback(true);
        setCurriculumData(fallbackData);
        setLoading(false);
      }
    };

    fetchCurriculum();
  }, []);

  // Fetch subject details from API
  const fetchSubjectDetails = async (subjectName) => {
    if (!subjectName || subjectName === "All") {
      setSubjectDetails(null);
      return;
    }

    try {
      setLoadingSubject(true);

      // Try to get subject details from API
      const response = await fetch(
        `${API_URL}/api/agents/curriculum/subject/${encodeURIComponent(
          subjectName
        )}`,
        { headers: authHeaders() }
      );

      if (response.ok) {
        const apiResult = await response.json();
        if (apiResult.data && apiResult.data.length > 0) {
          setSubjectDetails({
            name: subjectName,
            results: apiResult.data,
          });
          setLoadingSubject(false);
          return;
        }
      }
    } catch (apiError) {
      console.log("API subject fetch failed:", apiError.message);
    }

    // Fallback: Use local data if API fails
    const subject = curriculumData?.curriculum_items?.find(
      (item) => item.name === subjectName
    );

    if (subject && Array.isArray(subject.topics) && subject.topics.length) {
      // Only show what the data actually contains - no generated filler text
      const detailedResults = subject.topics.map((topic) => ({
        topic: topic,
        learning_objectives: [],
        grade: subject.grade || "",
      }));

      setSubjectDetails({
        name: subjectName,
        results: detailedResults,
      });
    } else {
      setSubjectDetails(null);
    }

    setLoadingSubject(false);
  };

  // Enhanced filtering
  const filteredCurriculum = React.useMemo(() => {
    const items = curriculumData?.data || curriculumData?.curriculum_items;
    if (!Array.isArray(items)) return [];

    return items.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        String(item.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (item.topics &&
          Array.isArray(item.topics) &&
          item.topics.some(
            (topic) =>
              typeof topic === "string" &&
              topic.toLowerCase().includes(searchTerm.toLowerCase())
          ));

      const matchesSubject =
        selectedSubject === "All" || item.name === selectedSubject;

      return matchesSearch && matchesSubject;
    });
  }, [curriculumData, searchTerm, selectedSubject]);

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setSelectedSubject("All");
    setSubjectDetails(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="agent-container">
          <div className="agent-loading">
            <div className="loading-spinner"></div>
            <p>Loading curriculum data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="agent-container">
        <div className="agent-header">
          <h1 className="agent-title">Curriculum Viewer</h1>
          <p className="agent-subtitle">
            Explore educational content across different subjects and grade
            levels
          </p>

          {curriculumData &&
            (() => {
              const subjectCount = curriculumData.subjects?.length || 0;
              const gradeCount = countGradeLevels(curriculumData);
              const topicCount = countTopics(curriculumData);
              const stats = [
                { value: subjectCount, one: "Subject", many: "Subjects" },
                { value: gradeCount, one: "Grade Level", many: "Grade Levels" },
                { value: topicCount, one: "Topic", many: "Total Topics" },
              ].filter((stat) => stat.value > 0);
              if (!stats.length) return null;
              return (
                <div className="curriculum-stats">
                  {stats.map((stat) => (
                    <div className="stat-item" key={stat.many}>
                      <span className="stat-number">{stat.value}</span>
                      <span className="stat-label">
                        {stat.value === 1 ? stat.one : stat.many}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
        </div>

        {/* Search and Filter Controls */}
        <div className="curriculum-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search subjects or topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-container">
            <label htmlFor="subject-filter">Filter by Subject:</label>
            <select
              id="subject-filter"
              value={selectedSubject}
              onChange={(e) => {
                const subject = e.target.value;
                setSelectedSubject(subject);
                fetchSubjectDetails(subject);
              }}
              className="subject-filter"
            >
              <option value="All">All Subjects</option>
              {curriculumData?.subjects?.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
          </div>

          {(searchTerm || selectedSubject !== "All") && (
            <button
              onClick={clearSearch}
              className="back-button"
              style={{ alignSelf: "end", padding: "0.5rem 1rem" }}
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Curriculum Content */}
        <div className="curriculum-content">
          {filteredCurriculum.length === 0 ? (
            <div className="no-results">
              <h3>No curriculum items found</h3>
              <p>Try adjusting your search terms or filters</p>
              <button onClick={clearSearch} className="back-button">
                Show All Items
              </button>
            </div>
          ) : loadingSubject ? (
            <div className="agent-loading">
              <p>Loading subject details...</p>
            </div>
          ) : subjectDetails &&
            subjectDetails.results &&
            subjectDetails.results.length > 0 ? (
            <div className="subject-detail-view">
              <h2>{subjectDetails.name} Curriculum Details</h2>
              <p className="detail-count">
                Showing {subjectDetails.results.length} curriculum{" "}
                {subjectDetails.results.length === 1 ? "item" : "items"}
              </p>

              {subjectDetails.results.map((item, index) => (
                <div key={`detail-${index}`} className="subject-detail-item">
                  <h3 className="detail-topic">
                    {(typeof item.topic === "string" && item.topic) ||
                      `Topic ${index + 1}`}
                  </h3>

                  <div className="subject-detail-content">
                    <div className="detail-left-column">
                      {(() => {
                        const cleanedObjectives = cleanLearningObjectives(
                          item.learning_objectives
                        );
                        return cleanedObjectives.length > 0 ? (
                          <div className="detail-objectives">
                            <h4>Learning Objectives:</h4>
                            <ul>
                              {cleanedObjectives.map((obj, idx) => (
                                <li key={`obj-${idx}`}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    <div className="detail-right-column">
                      {typeof item.text === "string" && item.text.trim() && (
                        <div className="detail-text">
                          <h4>Content:</h4>
                          <p>{item.text}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {(item.grade || item.class) && (
                    <div className="detail-grade">
                      <strong>Grade Level:</strong> {item.grade || item.class}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="curriculum-grid">
              {filteredCurriculum.map((item, index) => {
                // Transform the item to match CurriculumContent expected format
                // Pass real, structured data only - no generated filler
                const formattedContent = {
                  topic: item.name,
                  grade: item.grade || item.class || "",
                  objectives: cleanLearningObjectives(item.learning_objectives),
                  topics: Array.isArray(item.topics)
                    ? item.topics.filter(
                        (t) => typeof t === "string" && t.trim()
                      )
                    : [],
                  activities: Array.isArray(item.activities)
                    ? item.activities.filter(
                        (a) => typeof a === "string" && a.trim()
                      )
                    : [],
                };

                return (
                  <div
                    key={`${item.name}-${index}`}
                    className="curriculum-item-wrapper"
                  >
                    <CurriculumContent content={formattedContent} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Additional Information */}
        {curriculumData && (
          <div className="curriculum-info">
            <p className="last-updated">
              <strong>Last Updated:</strong>{" "}
              {curriculumData.last_updated || "Unknown"}
            </p>
            <p className="data-source">
              <strong>Data source:</strong>{" "}
              {usingFallback
                ? "Offline sample curriculum (server unavailable)"
                : "Curriculum database"}
            </p>
            {filteredCurriculum.length > 0 && (
              <p>
                <strong>Showing:</strong> {filteredCurriculum.length} of{" "}
                {(curriculumData.data || curriculumData.curriculum_items || [])
                  .length}{" "}
                items
              </p>
            )}
          </div>
        )}

        <div className="agent-actions">
          <button
            className="back-button"
            onClick={() => navigate("/student-dashboard")}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default CurriculumViewer;
